import React from "react";
import { cn } from "@/lib/utils";
import { brands as allBrands, getBrandById } from "@/lib/mock-data";
import { BrandLogo } from "./BrandLogo";

/**
 * BrandStack — renders an overlapping stack of brand monograms for a deal
 * that accepts multiple brands. Single-brand deals render a plain BrandLogo
 * so existing callers can use this everywhere without an extra branch.
 *
 * Props:
 *   • brandIds: string[]  — one or more brand IDs
 *   • size: 'xs' | 'sm' | 'md' | 'lg'  — chip size
 *   • showName: boolean   — optional label on the right
 *   • max: number         — show up to N stacked monograms; rest collapse to "+N"
 */
export function BrandStack({ brandIds, size = "md", showName = false, max = 3, className }) {
  const ids = (brandIds || []).filter(Boolean);
  if (ids.length <= 1) {
    return <BrandLogo brandId={ids[0]} size={size} showName={showName} className={className} />;
  }

  const sz = {
    xs: { w: 24, h: 24, txt: "text-[10px]", overlap: -8 },
    sm: { w: 32, h: 32, txt: "text-[12px]", overlap: -10 },
    md: { w: 40, h: 40, txt: "text-[14px]", overlap: -12 },
    lg: { w: 56, h: 56, txt: "text-h3",     overlap: -16 },
  }[size];

  const visible = ids.slice(0, max);
  const overflow = ids.length - visible.length;

  const brandsResolved = visible.map((id) => getBrandById(id)).filter(Boolean);
  const primaryBrand = brandsResolved[0];

  return (
    <div className={cn("flex items-center gap-3", className)}>
      <div className="flex items-center">
        {brandsResolved.map((b, i) => {
          const initial = b.name.split(" ").map((w) => w[0]).slice(0, 2).join("");
          return (
            <div
              key={b.id}
              title={b.name}
              style={{
                width: sz.w,
                height: sz.h,
                backgroundColor: b.accent,
                marginLeft: i === 0 ? 0 : sz.overlap,
                zIndex: brandsResolved.length - i,
              }}
              className={cn(
                "shrink-0 rounded-md flex items-center justify-center text-white font-bold ring-2 ring-white",
                sz.txt
              )}
            >
              {initial}
            </div>
          );
        })}
        {overflow > 0 && (
          <div
            style={{
              width: sz.w,
              height: sz.h,
              marginLeft: sz.overlap,
              zIndex: 0,
            }}
            className={cn(
              "shrink-0 rounded-md flex items-center justify-center bg-surface-alt text-ink-muted font-bold ring-2 ring-white border border-line",
              sz.txt
            )}
          >
            +{overflow}
          </div>
        )}
      </div>
      {showName && primaryBrand && (
        <div className="flex flex-col min-w-0">
          <span className="text-h4 text-ink font-semibold leading-tight truncate">
            {brandsResolved.map(b => b.name).join(" + ")}
            {overflow > 0 && ` +${overflow}`}
          </span>
          <span className="text-caption text-ink-subtle">Multi-brand deal · {ids.length} retailers</span>
        </div>
      )}
    </div>
  );
}

export default BrandStack;
